import { LOCALES, type Locale } from '@freightapi/i18n/namespaces'
import { Button, cx, EmptyState, Spinner } from '@freightapi/ui'
import { createRootRoute, Link, Outlet } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useConsoleI18n } from '../lib/locale'

/**
 * The shell every screen sits in: the product name, the language switch and the error and
 * not-found boundaries. The signed-in chrome — the navigation from `src/nav.ts` — lives in the
 * dashboard layout, because `/login`, `/signup` and `/welcome` are reached without a session.
 */
export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  pendingComponent: RoutePending,
})

function RootLayout() {
  return (
    <Shell>
      <Outlet />
    </Shell>
  )
}

export function Shell({ children }: { readonly children: ReactNode }) {
  const { t } = useConsoleI18n()

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="sticky top-0 z-40 border-line border-b bg-surface">
        <div className="mx-auto flex w-full max-w-screen-xl items-center justify-between gap-3 px-6 py-3">
          <Link to="/" className="font-semibold text-ink text-lead">
            {t('app.name')}
          </Link>
          <LanguageSwitch />
        </div>
      </header>

      <main className="mx-auto w-full max-w-screen-xl grow px-6 py-8">{children}</main>
    </div>
  )
}

function LanguageSwitch() {
  const { locale, setLocale, t } = useConsoleI18n()

  return (
    // A fieldset carries the "group" role natively; the legend names it for a screen reader while
    // the buttons stay the two-letter codes that are recognisable at a glance.
    <fieldset className="m-0 flex items-center gap-1 border-0 p-0">
      <legend className="sr-only">{t('language.label')}</legend>
      {LOCALES.map((option: Locale) => (
        <button
          key={option}
          type="button"
          lang={option}
          aria-label={t(`language.${option}`)}
          aria-current={option === locale ? 'true' : undefined}
          onClick={() => setLocale(option)}
          className={cx(
            'min-h-touch rounded-card px-3 font-medium text-body uppercase',
            option === locale ? 'bg-brand-50 text-brand-700' : 'text-muted hover:bg-canvas',
          )}
        >
          {option}
        </button>
      ))}
    </fieldset>
  )
}

function RouteError({ reset }: { readonly reset: () => void }) {
  const { t } = useConsoleI18n()
  return (
    <Shell>
      <EmptyState
        title={t('error.title')}
        description={t('error.body')}
        action={<Button onClick={reset}>{t('button.retry')}</Button>}
      />
    </Shell>
  )
}

function RouteNotFound() {
  const { t } = useConsoleI18n()
  return (
    <Shell>
      <EmptyState title={t('error.not_found.title')} description={t('error.not_found.body')} />
    </Shell>
  )
}

function RoutePending() {
  const { t } = useConsoleI18n()
  return (
    <div className="flex justify-center py-10">
      <Spinner label={t('loading')} size="lg" />
    </div>
  )
}
